import React, { useState, useEffect } from 'react';
import { ShieldCheckIcon, DevicePhoneMobileIcon, KeyIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { mfaService } from '../services/mfaService';
import { apiService } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';
import { RecaptchaVerifier, TotpSecret, onAuthStateChanged, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth } from '../config/firebase';
import { QRCodeSVG } from 'qrcode.react';

const MFA_CODE_LENGTH = 6;

interface MfaEnrollmentProps {
  onComplete?: () => void;
  onDismiss?: () => void;
}

export const MfaEnrollment: React.FC<MfaEnrollmentProps> = ({ onComplete, onDismiss }) => {
  const [enrollmentMethod, setEnrollmentMethod] = useState<'sms' | 'authenticator' | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [authenticatorCode, setAuthenticatorCode] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'method' | 'sms-phone' | 'sms-code' | 'authenticator-qr' | 'authenticator-code'>('method');

  useEffect(() => {
    // Initialize reCAPTCHA verifier for SMS
    if (enrollmentMethod === 'sms' && !recaptchaVerifier && step === 'sms-phone') {
      try {
        const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: () => {
            console.log('reCAPTCHA verified');
          },
        });
        setRecaptchaVerifier(verifier);
      } catch (error) {
        console.error('Failed to initialize reCAPTCHA:', error);
        toast.error('Failed to initialize SMS verification. Please refresh the page.');
      }
    }

    // Cleanup reCAPTCHA on unmount
    return () => {
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
      }
    };
  }, [enrollmentMethod, step]);

  const handleSmsEnrollment = async () => {
    if (!phoneNumber) {
      toast.error('Please enter a phone number');
      return;
    }

    // Validate phone number format (basic E.164 format)
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    if (!phoneRegex.test(phoneNumber)) {
      toast.error('Please enter a valid phone number in international format (e.g., +1234567890)');
      return;
    }

    setLoading(true);
    try {
      const vid = await mfaService.enrollSms(phoneNumber, recaptchaVerifier);
      setVerificationId(vid);
      setStep('sms-code');
      toast.success('Verification code sent to your phone');
    } catch (error: any) {
      console.error('SMS enrollment failed:', error);
      toast.error(error.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleSmsVerification = async () => {
    if (!smsCode || !verificationId) {
      toast.error('Please enter the verification code');
      return;
    }

    setLoading(true);
    try {
      await mfaService.verifySmsCode(verificationId, smsCode);
      toast.success('SMS MFA enrolled successfully!');
      onComplete?.();
    } catch (error: any) {
      console.error('SMS verification failed:', error);
      toast.error(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };


  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');

  const handleAuthenticatorEnrollment = async () => {
    setLoading(true);
    try {
      // Ensure we have a current user - wait for auth state if needed
      if (!auth.currentUser) {
        // Wait for auth state to be available
        await new Promise<void>((resolve, reject) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            if (user) {
              resolve();
            } else {
              reject(new Error('Not authenticated. Please refresh the page and log in again.'));
            }
          });
          // Timeout after 2 seconds
          setTimeout(() => {
            unsubscribe();
            reject(new Error('Authentication timeout. Please refresh the page.'));
          }, 2000);
        });
      }
      
      const result = await mfaService.enrollAuthenticator();
      setQrCodeUrl(result.qrCodeUrl);
      // Store the TotpSecret object directly
      setTotpSecret(result.secret);
      setStep('authenticator-qr');
    } catch (error: any) {
      console.error('Authenticator enrollment failed:', error);
      if (error.message?.includes('REQUIRES_RECENT_LOGIN')) {
        setNeedsReauth(true);
        toast.error('Please re-enter your password to continue with MFA setup.');
      } else {
        toast.error(error.message || 'Failed to generate QR code. Please refresh the page and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReauth = async () => {
    if (!reauthPassword) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('Not authenticated');
      }

      // Re-authenticate with email/password
      const credential = EmailAuthProvider.credential(user.email, reauthPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Now try enrollment again
      setNeedsReauth(false);
      setReauthPassword('');
      await handleAuthenticatorEnrollment();
    } catch (error: any) {
      console.error('Re-authentication failed:', error);
      if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password. Please try again.');
      } else {
        toast.error(error.message || 'Re-authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticatorVerification = async () => {
    if (!authenticatorCode || !totpSecret) {
      toast.error('Please enter the code from your authenticator app');
      return;
    }

    setLoading(true);
    try {
      // Pass the TotpSecret object directly (it should still be the Firebase TotpSecret object)
      await mfaService.verifyAuthenticatorCode(totpSecret, authenticatorCode);
      toast.success('Authenticator MFA enrolled successfully!');
      onComplete?.();
    } catch (error: any) {
      console.error('Authenticator verification failed:', error);
      if (error.message?.includes('REQUIRES_RECENT_LOGIN')) {
        setNeedsReauth(true);
        toast.error('Please re-enter your password to complete MFA setup.');
      } else {
        toast.error(error.message || 'Invalid code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === 'method') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <ShieldCheckIcon className="h-6 w-6 mr-2 text-blue-600" />
            Multi-Factor Authentication
          </h2>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
        <p className="text-gray-600 mb-6">
          For security, please set up multi-factor authentication. Choose your preferred method:
        </p>

        <div className="space-y-4">
          <button
            onClick={() => {
              setEnrollmentMethod('sms');
              setStep('sms-phone');
            }}
            className="w-full flex items-center px-4 py-3 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <DevicePhoneMobileIcon className="h-6 w-6 mr-3 text-blue-600" />
            <div className="text-left">
              <div className="font-medium text-gray-900">Use SMS</div>
              <div className="text-sm text-gray-500">Receive codes via text message</div>
            </div>
          </button>

          <button
            onClick={async () => {
              setEnrollmentMethod('authenticator');
              await handleAuthenticatorEnrollment();
            }}
            disabled={loading}
            className="w-full flex items-center px-4 py-3 border-2 border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors disabled:opacity-50"
          >
            <KeyIcon className="h-6 w-6 mr-3 text-green-600" />
            <div className="text-left">
              <div className="font-medium text-gray-900">Use Authenticator App</div>
              <div className="text-sm text-gray-500">Google Authenticator, Microsoft Authenticator, etc.</div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (step === 'sms-phone') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">SMS Setup</h2>
        <p className="text-gray-600 mb-4">
          Enter your phone number to receive verification codes. Make sure to include the country code.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              placeholder="+1234567890"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Format: +[country code][number] (e.g., +1234567890)
            </p>
          </div>

          <div id="recaptcha-container"></div>

          <div className="flex space-x-3">
            <button
              onClick={() => setStep('method')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={handleSmsEnrollment}
              disabled={loading || !phoneNumber}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Send Code'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'sms-code') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Verify Phone Number</h2>
        <p className="text-gray-600 mb-4">
          Enter the 6-digit code sent to {phoneNumber}
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              placeholder="123456"
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, MFA_CODE_LENGTH))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
              maxLength={MFA_CODE_LENGTH}
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setStep('sms-phone')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={handleSmsVerification}
              disabled={loading || smsCode.length !== MFA_CODE_LENGTH}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Verify'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'authenticator-qr') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Authenticator App Setup</h2>
        <p className="text-gray-600 mb-4">
          Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)
        </p>
        
        {qrCodeUrl && (
          <div className="mb-4 flex justify-center p-4 bg-white rounded-lg border border-gray-200">
            <QRCodeSVG 
              value={qrCodeUrl} 
              size={256}
              level="M"
              includeMargin={true}
            />
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={() => {
              setStep('method');
              setQrCodeUrl('');
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Back
          </button>
          <button
            onClick={() => setStep('authenticator-code')}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            I've Scanned the Code
          </button>
        </div>
      </div>
    );
  }

  // Re-authentication step
  if (needsReauth) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Re-authentication Required</h2>
        <p className="text-gray-600 mb-4">
          For security, please re-enter your password to continue with MFA setup.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              placeholder="Enter your password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => {
                setNeedsReauth(false);
                setReauthPassword('');
                setStep('method');
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleReauth}
              disabled={loading || !reauthPassword}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'authenticator-code') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Verify Authenticator Code</h2>
        <p className="text-gray-600 mb-4">
          Enter the 6-digit code from your authenticator app to complete setup.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              placeholder="123456"
              value={authenticatorCode}
              onChange={(e) => setAuthenticatorCode(e.target.value.replace(/\D/g, '').slice(0, MFA_CODE_LENGTH))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center text-2xl tracking-widest"
              maxLength={MFA_CODE_LENGTH}
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setStep('authenticator-qr')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={handleAuthenticatorVerification}
              disabled={loading || authenticatorCode.length !== MFA_CODE_LENGTH}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Verify & Complete'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
